import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { devtools } from 'zustand/middleware';

// Types
export interface Section {
  id: string;
  type: string;
  section_type?: string; // API field that we transform to type
  template_id?: string;
  title: string;
  subtitle?: string;
  position: number;
  is_visible: boolean;
  props: Record<string, any>;
  styles: Record<string, any>;
  responsive: {
    desktop?: boolean;
    tablet?: boolean;
    mobile?: boolean;
  };
  animations?: Record<string, any>;
  content?: Content[];
}

export interface Content {
  id: string;
  component_type: string;
  title?: string;
  description?: string;
  image_url?: string;
  thumbnail_url?: string;
  props: Record<string, any>;
  styles: Record<string, any>;
  position: number;
  is_visible: boolean;
}

export interface Template {
  id: string;
  category: string;
  name: string;
  description?: string;
  thumbnail?: string;
  config: any;
  is_premium: boolean;
}

interface HistoryState {
  past: Section[][];
  present: Section[];
  future: Section[][];
}

interface BuilderStore {
  // State
  sections: Section[];
  selectedId: string | null;
  isDragging: boolean;
  isLoading: boolean;
  isSaving: boolean;
  hasChanges: boolean;
  history: HistoryState;
  templates: Template[];
  activeDevice: 'desktop' | 'tablet' | 'mobile';

  // Section Actions
  addSection: (type: string, template?: Template | any, position?: number) => void;
  updateSection: (id: string, updates: Partial<Section>) => void;
  deleteSection: (id: string) => void;
  moveSection: (fromIndex: number, toIndex: number) => void;
  duplicateSection: (id: string) => void;
  toggleSectionVisibility: (id: string) => void;

  // Content Actions
  addContent: (sectionId: string, content: Omit<Content, 'id' | 'position'>) => void;
  updateContent: (sectionId: string, contentId: string, updates: Partial<Content>) => void;
  deleteContent: (sectionId: string, contentId: string) => void;
  moveContent: (sectionId: string, fromIndex: number, toIndex: number) => void;

  // Selection
  selectSection: (id: string | null) => void;

  // History
  undo: () => void;
  redo: () => void;
  saveHistory: () => void;

  // Device
  setActiveDevice: (device: 'desktop' | 'tablet' | 'mobile') => void;

  // Persistence
  loadSections: (sections: Section[]) => void;
  save: () => Promise<void>;
  publish: () => Promise<void>;

  // Templates
  loadTemplates: () => Promise<void>;

  // Utilities
  setIsDragging: (isDragging: boolean) => void;
  reset: () => void;
}

const API_BASE = import.meta.env.PROD
  ? '/api'  // Use relative path for production to work with proxy
  : (import.meta.env.VITE_API_URL || 'http://localhost:8000/api');

export const useBuilderStore = create<BuilderStore>()(
  devtools(
    immer((set, get) => ({
      // Initial state
      sections: [],
      selectedId: null,
      isDragging: false,
      isLoading: false,
      isSaving: false,
      hasChanges: false,
      history: {
        past: [],
        present: [],
        future: []
      },
      templates: [],
      activeDevice: 'desktop',

      // Section Actions
      addSection: (type, template, position) => {
        console.log('addSection called with:', { type, template, position });
        set((state) => {
          // Use template config if provided
          const config = template?.config || template;
          const templateType = config?.type || type;
          console.log('Using config:', config, 'templateType:', templateType);

          const newSection: Section = {
            id: `section_${Date.now()}`,
            type: templateType,
            template_id: template?.id || config?.id,
            title: config?.props?.title || `New ${templateType} Section`,
            subtitle: config?.props?.subtitle,
            position: position ?? state.sections.length,
            is_visible: true,
            props: config?.props || {},
            styles: config?.styles || {},
            responsive: config?.responsive || { desktop: true, tablet: true, mobile: true },
            animations: config?.animations || {},
            content: config?.content || []
          };

          // Adjust positions of other sections
          state.sections.forEach(section => {
            if (section.position >= newSection.position) {
              section.position++;
            }
          });

          state.sections.push(newSection);
          state.selectedId = newSection.id;
          state.hasChanges = true;
          console.log('Added new section:', newSection);
          console.log('Total sections:', state.sections.length);
          get().saveHistory();
        });
      },

      updateSection: (id, updates) => {
        set((state) => {
          const index = state.sections.findIndex(s => s.id === id);
          if (index !== -1) {
            // Ensure type field is preserved
            const currentType = state.sections[index].type || state.sections[index].section_type;
            Object.assign(state.sections[index], updates);
            // Make sure type doesn't get lost
            if (!state.sections[index].type && currentType) {
              state.sections[index].type = currentType;
            }
            state.hasChanges = true;
          }
        });
      },

      deleteSection: (id) => {
        set((state) => {
          const index = state.sections.findIndex(s => s.id === id);
          if (index !== -1) {
            const deletedPosition = state.sections[index].position;
            state.sections.splice(index, 1);

            // Adjust positions
            state.sections.forEach(section => {
              if (section.position > deletedPosition) {
                section.position--;
              }
            });

            if (state.selectedId === id) {
              state.selectedId = null;
            }
            state.hasChanges = true;
            get().saveHistory();
          }
        });
      },

      moveSection: (fromIndex, toIndex) => {
        set((state) => {
          const section = state.sections[fromIndex];
          state.sections.splice(fromIndex, 1);
          state.sections.splice(toIndex, 0, section);

          // Update positions
          state.sections.forEach((section, index) => {
            section.position = index;
          });

          state.hasChanges = true;
          get().saveHistory();
        });
      },

      duplicateSection: (id) => {
        set((state) => {
          const section = state.sections.find(s => s.id === id);
          if (section) {
            const newSection: Section = {
              ...JSON.parse(JSON.stringify(section)), // Deep clone
              id: `section_${Date.now()}`,
              position: section.position + 1,
              title: `${section.title} (Copy)`
            };

            // Adjust positions
            state.sections.forEach(s => {
              if (s.position > section.position) {
                s.position++;
              }
            });

            state.sections.push(newSection);
            state.selectedId = newSection.id;
            state.hasChanges = true;
            get().saveHistory();
          }
        });
      },

      toggleSectionVisibility: (id) => {
        set((state) => {
          const section = state.sections.find(s => s.id === id);
          if (section) {
            section.is_visible = !section.is_visible;
            state.hasChanges = true;
          }
        });
      },

      // Content Actions
      addContent: (sectionId, content) => {
        set((state) => {
          const section = state.sections.find(s => s.id === sectionId);
          if (section) {
            if (!section.content) section.content = [];
            const newContent: Content = {
              ...content,
              id: `content_${Date.now()}`,
              position: section.content.length,
              is_visible: true
            };
            section.content.push(newContent);
            state.hasChanges = true;
          }
        });
      },

      updateContent: (sectionId, contentId, updates) => {
        set((state) => {
          const section = state.sections.find(s => s.id === sectionId);
          if (section && section.content) {
            const content = section.content.find(c => c.id === contentId);
            if (content) {
              Object.assign(content, updates);
              state.hasChanges = true;
            }
          }
        });
      },

      deleteContent: (sectionId, contentId) => {
        set((state) => {
          const section = state.sections.find(s => s.id === sectionId);
          if (section && section.content) {
            const index = section.content.findIndex(c => c.id === contentId);
            if (index !== -1) {
              section.content.splice(index, 1);
              // Update positions
              section.content.forEach((content, i) => {
                content.position = i;
              });
              state.hasChanges = true;
            }
          }
        });
      },

      moveContent: (sectionId, fromIndex, toIndex) => {
        set((state) => {
          const section = state.sections.find(s => s.id === sectionId);
          if (section && section.content) {
            const content = section.content[fromIndex];
            section.content.splice(fromIndex, 1);
            section.content.splice(toIndex, 0, content);

            // Update positions
            section.content.forEach((content, i) => {
              content.position = i;
            });
            state.hasChanges = true;
          }
        });
      },

      // Selection
      selectSection: (id) => {
        set({ selectedId: id });
      },

      // History
      undo: () => {
        set((state) => {
          if (state.history.past.length > 0) {
            const previous = state.history.past[state.history.past.length - 1];
            state.history.past.pop();
            state.history.future.unshift(state.sections);
            state.sections = previous;
            state.hasChanges = true;
          }
        });
      },

      redo: () => {
        set((state) => {
          if (state.history.future.length > 0) {
            const next = state.history.future[0];
            state.history.future.shift();
            state.history.past.push(state.sections);
            state.sections = next;
            state.hasChanges = true;
          }
        });
      },

      saveHistory: () => {
        set((state) => {
          state.history.past.push(JSON.parse(JSON.stringify(state.sections)));
          state.history.future = [];

          // Keep only last 50 history items
          if (state.history.past.length > 50) {
            state.history.past.shift();
          }
        });
      },

      // Device
      setActiveDevice: (device) => {
        set({ activeDevice: device });
      },

      // Persistence
      loadSections: (sections) => {
        console.log('LoadSections - raw input:', sections.map((s: any) => ({
          id: s.id,
          type: s.type,
          section_type: s.section_type,
          title: s.title,
          props: typeof s.props
        })));

        // Transform section_type to type and parse JSON strings
        const transformedSections = sections.map((section: any) => {
          const type = section.type || section.section_type || '';
          if (!type) {
            console.warn(`Section ${section.id} has no type or section_type!`, section);
          }

          // Parse JSON strings if they exist
          let props = section.props || {};
          let styles = section.styles || {};
          let responsive = section.responsive || {};
          let animations = section.animations || {};

          // Parse props if it's a string
          if (typeof props === 'string') {
            try {
              props = JSON.parse(props);
            } catch (e) {
              console.error(`Failed to parse props for section ${section.id}:`, e);
              props = {};
            }
          }

          // Parse styles if it's a string
          if (typeof styles === 'string') {
            try {
              styles = JSON.parse(styles);
            } catch (e) {
              console.error(`Failed to parse styles for section ${section.id}:`, e);
              styles = {};
            }
          }

          // Parse responsive if it's a string
          if (typeof responsive === 'string') {
            try {
              responsive = JSON.parse(responsive);
            } catch (e) {
              console.error(`Failed to parse responsive for section ${section.id}:`, e);
              responsive = { desktop: true, tablet: true, mobile: true };
            }
          }

          // Parse animations if it's a string
          if (typeof animations === 'string') {
            try {
              animations = JSON.parse(animations);
            } catch (e) {
              console.error(`Failed to parse animations for section ${section.id}:`, e);
              animations = {};
            }
          }

          // Parse content items if they have JSON strings
          let content = section.content || [];
          if (Array.isArray(content)) {
            content = content.map((item: any) => {
              let itemProps = item.props || {};
              let itemStyles = item.styles || {};

              if (typeof itemProps === 'string') {
                try {
                  itemProps = JSON.parse(itemProps);
                } catch (e) {
                  itemProps = {};
                }
              }

              if (typeof itemStyles === 'string') {
                try {
                  itemStyles = JSON.parse(itemStyles);
                } catch (e) {
                  itemStyles = {};
                }
              }

              return {
                ...item,
                props: itemProps,
                styles: itemStyles
              };
            });
          }

          return {
            ...section,
            type: type,
            section_type: section.section_type, // Preserve original for API calls
            props,
            styles,
            responsive,
            animations,
            content
          };
        }).sort((a, b) => a.position - b.position);

        console.log('LoadSections - transformed:', transformedSections.map(s => ({
          id: s.id,
          type: s.type,
          section_type: s.section_type,
          title: s.title,
          hasType: !!s.type,
          props: s.props
        })));

        set({
          sections: transformedSections,
          hasChanges: false,
          history: {
            past: [],
            present: transformedSections,
            future: []
          }
        });
      },

      save: async () => {
        const { sections } = get();
        set({ isSaving: true });

        try {
          // Transform sections for API
          const apiSections = sections.map(s => {
            console.log(`Saving section ${s.id} (${s.type}):`, s.props);

            // Map section type properly - handle premium sections
            let sectionType = s.type;
            if (sectionType === 'premium_cards') {
              // Store as feature_cards but keep the variant in props
              const updatedProps = { ...s.props, cardVariant: 'premium' };
              return {
                ...s,
                section_type: 'premium_cards', // Now supported in DB
                type: sectionType,
                props: JSON.stringify(updatedProps),
                styles: JSON.stringify(s.styles || {}),
                responsive: JSON.stringify(s.responsive || {}),
                animations: JSON.stringify(s.animations || {}),
                content: s.content?.map((item: any) => ({
                  ...item,
                  props: JSON.stringify(item.props || {}),
                  styles: JSON.stringify(item.styles || {})
                })) || []
              };
            }

            return {
              ...s,
              section_type: sectionType,
              type: sectionType,
              props: JSON.stringify(s.props || {}),
              styles: JSON.stringify(s.styles || {}),
              responsive: JSON.stringify(s.responsive || {}),
              animations: JSON.stringify(s.animations || {}),
              content: s.content?.map((item: any) => ({
                ...item,
                props: JSON.stringify(item.props || {}),
                styles: JSON.stringify(item.styles || {})
              })) || []
            };
          });

          // Use the API_BASE which will be /api in production
          const endpoint = `${API_BASE}/homepage/builder/save`;

          console.log(`Saving to: ${endpoint}`);
          const _saveHeaders: Record<string, string> = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          };
          const _saveToken = localStorage.getItem('admin_token');
          if (_saveToken) _saveHeaders['Authorization'] = `Bearer ${_saveToken}`;
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: _saveHeaders,
            body: JSON.stringify({ sections: apiSections }),
            mode: 'cors'
          });

          // Check if response is ok
          if (response.ok) {
            try {
              const data = await response.json();
              console.log('Save successful:', data);
              set({ hasChanges: false });

              // Show success message
              if ((window as any).showNotification) {
                (window as any).showNotification('Changes saved successfully!', 'success');
              }

              return data;
            } catch (jsonError) {
              // Response is OK but not JSON, still consider it success
              console.log('Save successful (non-JSON response)');
              set({ hasChanges: false });
              return { success: true };
            }
          }

          // Handle error response
          const errorText = await response.text();
          const error = new Error(`${response.status}: ${errorText}`);
          console.error(`Save failed:`, error);
          throw error;
        } catch (error: any) {
          console.error('Save error:', error);

          // Show error message
          if ((window as any).showNotification) {
            (window as any).showNotification(
              `Failed to save: ${error.message}`,
              'error'
            );
          }

          throw error;
        } finally {
          set({ isSaving: false });
        }
      },

      publish: async () => {
        const { sections } = get();
        set({ isSaving: true });

        try {
          // Transform sections for API: stringify JSON fields and map type to section_type
          const apiSections = sections.map(s => ({
            ...s,
            section_type: s.type, // Map type back to section_type
            type: undefined, // Remove type field to avoid confusion
            props: JSON.stringify(s.props || {}),
            styles: JSON.stringify(s.styles || {}),
            responsive: JSON.stringify(s.responsive || {}),
            animations: JSON.stringify(s.animations || {}),
            // Stringify content items' JSON fields
            content: s.content?.map((item: any) => ({
              ...item,
              props: JSON.stringify(item.props || {}),
              styles: JSON.stringify(item.styles || {})
            })) || []
          }));

          const _publishHeaders: Record<string, string> = {
            'Content-Type': 'application/json',
          };
          const _publishToken = localStorage.getItem('admin_token');
          if (_publishToken) _publishHeaders['Authorization'] = `Bearer ${_publishToken}`;
          const response = await fetch(`${API_BASE}/homepage/builder/publish`, {
            method: 'POST',
            headers: _publishHeaders,
            body: JSON.stringify({ sections: apiSections })
          });

          if (response.ok) {
            set({ hasChanges: false });
            return;
          }
          throw new Error('Failed to publish');
        } catch (error) {
          console.error('Publish error:', error);
          throw error;
        } finally {
          set({ isSaving: false });
        }
      },

      // Templates
      loadTemplates: async () => {
        try {
          const response = await fetch(`${API_BASE}/homepage/templates`);
          if (response.ok) {
            const templates = await response.json();
            set({ templates });
          }
        } catch (error) {
          console.error('Failed to load templates:', error);
        }
      },

      // Utilities
      setIsDragging: (isDragging) => {
        set({ isDragging });
      },

      reset: () => {
        set({
          sections: [],
          selectedId: null,
          isDragging: false,
          isLoading: false,
          isSaving: false,
          hasChanges: false,
          history: {
            past: [],
            present: [],
            future: []
          },
          activeDevice: 'desktop'
        });
      }
    }))
  )
);

// Expose store to window for debugging (only in development)
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (window as any).builderStore = useBuilderStore;
}